import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

export type NotificationSource = "changes" | "system" | "web_alerts";

export interface ChangeLog {
    source: "changes";
    id: number;
    action_by_id: string;
    action_by: string;
    group_name: string;
    action: string;
    is_read: boolean;
    created_at: string;
    ticker: string;
    meta: {
        before: any | null;
        after: any | null;
    };
}

export interface SystemLog {
    source: "system";
    id: number;
    created_at: string;
    level: string;
    module: string;
    text: string;
    ticker_name?: string;
    tab_id?: string | number;
}

export interface WebAlert {
    source: "web_alerts";
    id: number;
    created_at: string;
    level: string;
    text: string;
    is_read: boolean;
}

export type NotificationLog = ChangeLog | SystemLog | WebAlert;

interface NotificationsState {
    changes: {
        logs: ChangeLog[];
        hasMore: boolean;
        lastPage: number;
        unread: number;
        loading: boolean;
    };
    system: {
        logs: SystemLog[];
        hasMore: boolean;
        lastPage: number;
        unread: number;
        loading: boolean;
    };
    web_alerts: {
        logs: WebAlert[];
        hasMore: boolean;
        lastPage: number;
        unread: number;
        loading: boolean;
    };

    totalUnread: number;
    globalLoading: boolean;
    filters: {
        changes: {
            robot_id?: string | null;
            group_name: string;
            action: string;
            action_by_id: string;
            ticker: string;
        };
        system: {
            robot_id?: string | null;
            level: string;
            from_date: string;
            to_date: string;
            linked: boolean;
        };
        web_alerts: {
            robot_id?: string | null;
            level: string;
        };
    };
}

const initialState: NotificationsState = {
    changes: {
        logs: [],
        hasMore: true,
        lastPage: 1,
        unread: 0,
        loading: false,
    },
    system: { logs: [], hasMore: true, lastPage: 1, unread: 0, loading: false },
    web_alerts: {
        logs: [],
        hasMore: true,
        lastPage: 1,
        unread: 0,
        loading: false,
    },
    totalUnread: 0,
    globalLoading: false,
    filters: {
        changes: {
            robot_id: "",
            group_name: "",
            action: "",
            action_by_id: "",
            ticker: "",
        },
        system: {
            robot_id: "",
            level: "",
            from_date: "",
            to_date: "",
            linked: undefined,
        },
        web_alerts: {
            robot_id: "",
            level: "",
        },
    },
};

const notificationsSlice = createSlice({
    name: "notifications",
    initialState,
    reducers: {
        setLogs: (
            state,
            action: PayloadAction<{
                source: NotificationSource;
                logs: NotificationLog[];
            }>,
        ) => {
            const { source, logs } = action.payload;
            (state[source].logs as NotificationLog[]) = logs;
        },

        appendLogs: (
            state,
            action: PayloadAction<{
                source: NotificationSource;
                logs: NotificationLog[];
            }>,
        ) => {
            const { source, logs } = action.payload;
            (state[source].logs as NotificationLog[]).push(...logs);
        },
        prependLogs: (
            state,
            action: PayloadAction<{ source: NotificationSource; logs: any[] }>,
        ) => {
            const { source, logs } = action.payload;

            if (!state[source]) return;
            state[source].logs = [...logs, ...state[source].logs];
        },
        setHasMore: (
            state,
            action: PayloadAction<{
                source: NotificationSource;
                hasMore: boolean;
            }>,
        ) => {
            state[action.payload.source].hasMore = action.payload.hasMore;
        },

        setLastPage: (
            state,
            action: PayloadAction<{ source: NotificationSource; page: number }>,
        ) => {
            state[action.payload.source].lastPage = action.payload.page;
        },

        setUnread: (
            state,
            action: PayloadAction<Partial<Record<string, number>>>,
        ) => {
            const {
                totalUnread,
                changesUnread,
                systemUnread,
                webAlertsUnread,
            } = action.payload;

            if (totalUnread !== undefined) state.totalUnread = totalUnread;
            if (changesUnread !== undefined)
                state.changes.unread = changesUnread;
            if (systemUnread !== undefined) state.system.unread = systemUnread;
            if (webAlertsUnread !== undefined)
                state.web_alerts.unread = webAlertsUnread;
        },

        setLoading: (
            state,
            action: PayloadAction<{
                source?: NotificationSource;
                loading: boolean;
            }>,
        ) => {
            const { source, loading } = action.payload;
            if (source) {
                state[source].loading = loading;
            }
            state.globalLoading = loading;
        },

        markAsReadSuccess: (
            state,
            action: PayloadAction<{ source: NotificationSource; id?: number }>,
        ) => {
            const { source, id } = action.payload;
            const logs = state[source].logs as NotificationLog[];

            if (id !== undefined) {
                const log = logs.find((item) => item.id === id);
                if (log && "is_read" in log) {
                    (log as any).is_read = true;
                }
            } else {
                logs.forEach((item) => {
                    if ("is_read" in item) {
                        (item as any).is_read = true;
                    }
                });
                state[source].unread = 0;
            }

            state.totalUnread =
                state.changes.unread +
                state.system.unread +
                state.web_alerts.unread;
        },

        resetNotifications: (state) => {
            Object.assign(state, initialState);
        },

        clearLogs: (state, action: PayloadAction<NotificationSource>) => {
            const source = action.payload;
            state[source].logs = [];
            state[source].hasMore = true;
            state[source].lastPage = 1;
        },
        setFilters: (
            state,
            action: PayloadAction<{
                source: NotificationSource;
                filters: any;
            }>,
        ) => {
            state.filters[action.payload.source] = action.payload.filters;
        },
        loadNotifications: (
            state,
            action: PayloadAction<{ source: NotificationSource; page: number }>,
        ) => {},
    },
});

export const {
    setLogs,
    appendLogs,
    setHasMore,
    setLastPage,
    setUnread,
    setLoading,
    markAsReadSuccess,
    loadNotifications,
    resetNotifications,
    clearLogs,
    prependLogs,
    setFilters,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
